import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, HelpTip, errorMessage, grantWarningsFromError } from '@dataroom/shared';
import type {
  CategoryNode,
  FolderAudience,
  GrantWarning,
  SetFolderAudienceRequest,
} from '@dataroom/shared';
import { useFolderAudiences, useSetFolderAudience, type FolderTier } from '@/api/hooks';
import { categoryNodesById } from '@dataroom/shared';
import { FOLDER_AUDIENCES, audienceLabel, audienceRank } from '@/lib/folder-audience';
import { GrantWarningList } from './GrantWarningList';
import { Modal } from './Modal';

type Choice = FolderTier | 'inherit';

/** The choice the folder is on now: its own audience, or "same as the folder it
 * is in" when it has none. Null when it was tiered some other way (a stage the
 * dialog does not offer) — nothing is pre-selected then. */
function currentChoice(audience: FolderAudience, isSubfolder: boolean): Choice | null {
  if (audience.ownTier === null) return isSubfolder ? 'inherit' : 'due_diligence';
  return FOLDER_AUDIENCES.some((a) => a.value === audience.ownTier)
    ? (audience.ownTier as FolderTier)
    : null;
}

/**
 * "Change audience" — who gets a folder by default (its tier, ADR-0010 §2),
 * after it was created. Same options and wording as "New folder"; a folder
 * inside another can also follow that folder. Making material reach buyers
 * EARLIER is the advise + acknowledge gate (ADR-0010 §3): the server answers 409
 * with warnings, shown with "Proceed anyway", which resends acknowledged.
 */
export function ChangeAudienceDialog({
  roomId,
  folderName,
  audience,
  parent,
  onClose,
}: {
  roomId: string;
  folderName: string;
  audience: FolderAudience;
  /** The folder it is in (null at the top level). */
  parent: { name: string; audience: FolderAudience } | null;
  onClose: () => void;
}) {
  const save = useSetFolderAudience(roomId);
  const initial = currentChoice(audience, parent !== null);
  const [choice, setChoice] = useState<Choice | null>(initial);
  const [warnings, setWarnings] = useState<GrantWarning[]>([]);
  const groupName = useId();

  const send = (acknowledgeWarnings: boolean) => {
    if (choice === null) return;
    const body: SetFolderAudienceRequest = {
      tier: choice === 'inherit' ? null : choice,
      ...(acknowledgeWarnings ? { acknowledgeWarnings: true } : {}),
    };
    save.mutate(
      { tagId: audience.id, body },
      {
        onSuccess: onClose,
        onError: (err) => setWarnings(grantWarningsFromError(err)),
      },
    );
  };

  // What the folder's audience would be with this choice.
  const chosenTier =
    choice === 'inherit' ? (parent?.audience.effectiveTier ?? null) : (choice ?? null);
  const beforeDueDiligence =
    chosenTier !== null && audienceRank(chosenTier) < audienceRank('due_diligence');
  const unchanged = choice === initial;
  const error = warnings.length === 0 && save.isError ? errorMessage(save.error) : null;

  const options: { value: Choice; label: string; hint: string }[] = [
    ...(parent
      ? [
          {
            value: 'inherit' as const,
            label: `Same as “${parent.name}”`,
            hint: `Right now: ${audienceLabel(parent.audience.effectiveTier).toLowerCase()}.`,
          },
        ]
      : []),
    ...FOLDER_AUDIENCES,
  ];

  return (
    <Modal
      title="Change audience"
      description={`Who gets “${folderName}” by default.`}
      onClose={onClose}
      footer={
        warnings.length > 0 ? (
          <div className="w-full">
            <GrantWarningList
              warnings={warnings}
              pending={save.isPending}
              onProceed={() => send(true)}
              onCancel={() => setWarnings([])}
            />
          </div>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button
              onClick={() => send(false)}
              disabled={choice === null || unchanged || save.isPending}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </>
        )
      }
    >
      <div className="space-y-4">
        <p className="text-sm">
          Right now: <span className="font-medium">{audienceLabel(audience.effectiveTier)}</span>
          {audience.ownTier === null && parent ? ` (same as “${parent.name}”)` : null}.
        </p>
        <fieldset className="space-y-2" disabled={save.isPending || warnings.length > 0}>
          <legend className="text-sm font-medium">Who gets it by default</legend>
          {options.map((o) => (
            <label key={o.value} className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name={groupName}
                className="mt-1"
                checked={choice === o.value}
                onChange={() => setChoice(o.value)}
              />
              <span>
                {o.label}
                <span className="block text-xs text-muted-foreground">{o.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>
        {beforeDueDiligence ? (
          <p className="text-sm text-muted-foreground">
            The AI files new uploads only into due-diligence folders, so it will not file anything
            into this folder: you add its documents yourself. Buyers already invited see it only if
            their access includes this folder.
          </p>
        ) : null}
        {error ? (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * The selected folder's audience under the admin's folder tree, with its
 * "Change audience" action (a firm-wide folder shared by every room shows its
 * audience only). Renders nothing until the audiences load, or for a folder
 * that is not a real one (the synthetic "Uncategorized" bucket).
 */
export function FolderAudienceSummary({
  roomId,
  folderId,
  categories,
}: {
  roomId: string;
  folderId: string;
  categories: CategoryNode[];
}) {
  const audiences = useFolderAudiences(roomId);
  const [open, setOpen] = useState(false);
  const byId = new Map((audiences.data ?? []).map((a) => [a.id, a] as const));
  const audience = byId.get(folderId);
  if (!audience) return null;
  const nodes = categoryNodesById(categories);
  const name = nodes.get(folderId)?.name ?? 'this folder';
  const parentAudience = audience.parentId ? byId.get(audience.parentId) : undefined;
  const parent = parentAudience
    ? { name: nodes.get(parentAudience.id)?.name ?? 'the folder it is in', audience: parentAudience }
    : null;

  return (
    <div className="space-y-2 border-t border-line pt-3 text-sm">
      <p>
        <span className="text-muted-foreground">Who gets “{name}”: </span>
        <span className="font-medium">{audienceLabel(audience.effectiveTier)}</span>
        <HelpTip id="admin.data.change-audience" className="ml-1.5" />
      </p>
      {audience.editable ? (
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          Change audience
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          A folder shared by every room of your firm — its audience is set for all of them.
        </p>
      )}
      {/* Portaled: the folder tree card is sticky (its own stacking context), so
          a dialog rendered inside it would sit under the documents card. */}
      {open
        ? createPortal(
            <ChangeAudienceDialog
              roomId={roomId}
              folderName={name}
              audience={audience}
              parent={parent}
              onClose={() => setOpen(false)}
            />,
            document.body,
          )
        : null}
    </div>
  );
}
